import { useTranslation } from "react-i18next";
import { Card, CardContent, cn } from "@tellescope/ui";
import { Badge, Button } from "@tellescope/design-system";
import { formatTime } from "@tellescope/utils";
import { Calendar, CalendarPlus, Clock, MapPin, X } from "lucide-react";
import type { CalendarEvent } from "@tellescope/types-models";
import { detectVideoIntegration } from "../../../../shared/utils/detectVideoIntegration";
import { useEventStatus } from "../../../hooks/useEventStatus";
import { useTemplateContext } from "../../../hooks/useTemplateContext";
import { getCancelGate, getRescheduleGate } from "../../../utils/appointmentActionGates";
import { useEnduserStore } from "../../../store";
import { interpolateText } from "@tellescope/utils";
import { ASYNC_SOURCES } from "../../../../shared/template";
import { VideoCallButton } from "../../VideoCallButton";

export interface AppointmentCardProps {
  event: CalendarEvent;
  allowCancel?: boolean;
  allowReschedule?: boolean;
  rescheduleUrl?: string;
  hideVideoAfterMinutes?: number;
  hostUserUrl?: string;
  onCancelClick?: (event: CalendarEvent) => void;
  onRescheduleClick?: (event: CalendarEvent) => void;
  onJoinVideoCall?: (event: CalendarEvent) => void;
  onViewDetails?: (event: CalendarEvent) => void;
}

export const AppointmentCard = ({
  event,
  allowCancel,
  allowReschedule,
  rescheduleUrl,
  hideVideoAfterMinutes,
  hostUserUrl,
  onCancelClick,
  onRescheduleClick,
  onJoinVideoCall,
  onViewDetails,
}: AppointmentCardProps) => {
  const { t } = useTranslation("events");
  const templateContext = useTemplateContext();
  const startTime = formatTime(event.startTimeInMS);
  const endTime = formatTime(event.startTimeInMS + event.durationInMinutes * 60 * 1000);

  const currentEnduser = useEnduserStore((state) => state.currentEnduser);
  const { isCancelled, isRescheduled, isUpcoming, canJoinVideo } = useEventStatus(event, currentEnduser);
  const videoIntegration = detectVideoIntegration(event, hostUserUrl);

  const eventEndTimeInMS = event.startTimeInMS + event.durationInMinutes * 60 * 1000;
  const isVideoHidden =
    hideVideoAfterMinutes !== undefined && Date.now() > eventEndTimeInMS + hideVideoAfterMinutes * 60 * 1000;

  const cancelGate = getCancelGate(event, allowCancel, isUpcoming, isCancelled);
  const showCancelButton = cancelGate.visible;

  // Hide reschedule entirely when preventRescheduleInPortal is explicitly true.
  // Deliberately separate from getRescheduleGate below — this gates the post-cancellation
  // external link (isCancelled), that gates the in-app action (!isCancelled); they can't overlap.
  const showRescheduleButton = isCancelled && !!rescheduleUrl && event.preventRescheduleInPortal !== true;

  const rescheduleGate = getRescheduleGate(event, allowReschedule, isUpcoming, isCancelled);
  const showRescheduleAction = rescheduleGate.visible;

  // Interpolate reschedule URL with template context
  const interpolatedRescheduleUrl = rescheduleUrl
    ? interpolateText(rescheduleUrl, templateContext, { deferredSources: ASYNC_SOURCES })
    : undefined;

  return (
    <Card
      className={cn("overflow-hidden cursor-pointer hover:shadow-md transition-shadow", isCancelled && "opacity-75")}
      onClick={() => onViewDetails?.(event)}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          onViewDetails?.(event);
        }
      }}
    >
      <CardContent className="px-4">
        <div className="flex gap-4">
          {/* Date badge */}
          <div className="flex-shrink-0 text-center">
            <div
              className={cn(
                "w-14 h-14 rounded-lg flex flex-col items-center justify-center",
                isCancelled ? "bg-muted" : "bg-primary/10"
              )}
            >
              <Calendar className={cn("h-3.5 w-3.5", isCancelled ? "text-muted-foreground" : "text-primary")} />
              <span className={cn("text-xs font-medium", isCancelled ? "text-muted-foreground" : "text-primary")}>
                {new Date(event.startTimeInMS).toLocaleDateString(undefined, { month: "short" })}
              </span>
              <span className={cn("text-sm font-bold", isCancelled ? "text-muted-foreground" : "text-primary")}>
                {new Date(event.startTimeInMS).getDate()}
              </span>
            </div>
          </div>

          {/* Event details */}
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2">
              <h4 className={cn("font-medium text-sm truncate", isCancelled && "line-through text-muted-foreground")}>
                {event.displayTitle || event.title}
              </h4>
              {isCancelled && (
                <Badge color="destructive" size="sm">
                  {t("Cancelled")}
                </Badge>
              )}
              {isRescheduled && !isCancelled && (
                <Badge variant="soft" color="secondary" className="text-xs px-1.5 py-0 h-5">
                  {t("Rescheduled")}
                </Badge>
              )}
            </div>

            <div className="flex items-center gap-1 text-xs text-muted-foreground mt-1">
              <Clock className="h-3 w-3" />
              <span>
                {startTime} - {endTime}
              </span>
            </div>

            {event.location && (
              <div className="flex items-center gap-1 text-xs text-muted-foreground mt-1">
                <MapPin className="h-3 w-3" />
                <span className="truncate">{event.location}</span>
              </div>
            )}

            {/* Video call button - only show for non-cancelled events within the configured time window */}
            {videoIntegration.hasAnyVideo && !isCancelled && !isVideoHidden && videoIntegration.videoType && (
              <div onClick={(e) => e.stopPropagation()}>
                <VideoCallButton
                  event={event}
                  videoType={videoIntegration.videoType}
                  videoUrl={videoIntegration.primaryVideoUrl}
                  size="sm"
                  disabled={videoIntegration.videoType === "twilio" && !canJoinVideo}
                  onTwilioClick={onJoinVideoCall}
                />
              </div>
            )}

            {/* Action buttons */}
            <div className="flex items-center gap-2 mt-2">
              {showCancelButton && (
                <Button
                  variant="outline"
                  color="destructive"
                  size="sm"
                  className="h-7 text-xs"
                  disabled={cancelGate.disabled}
                  onClick={(e) => {
                    e.stopPropagation();
                    onCancelClick?.(event);
                  }}
                >
                  <X className="h-3 w-3 mr-1" />
                  {t("Cancel")}
                </Button>
              )}

              {showRescheduleButton && interpolatedRescheduleUrl && (
                <a
                  href={interpolatedRescheduleUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-1 h-7 px-3 text-xs font-medium text-primary border border-primary/30 rounded-md hover:bg-primary/10 transition-colors"
                  onClick={(e) => e.stopPropagation()}
                >
                  <CalendarPlus className="h-3 w-3" />
                  {t("Reschedule")}
                </a>
              )}

              {showRescheduleAction && (
                <Button
                  variant="outline"
                  size="sm"
                  className="h-7 text-xs"
                  disabled={rescheduleGate.disabled}
                  onClick={(e) => {
                    e.stopPropagation();
                    onRescheduleClick?.(event);
                  }}
                >
                  <CalendarPlus className="h-3 w-3 mr-1" />
                  {t("Reschedule")}
                </Button>
              )}
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};
